import type { Mutable, ValuesType } from 'utility-types'

import { isArray, objectEntries, type ReadonlyRecord } from '../../utils'

import type { CustomizedTable } from '../../domain'

import type { TablePersistenceService } from '../../application'

import { BaseWebStorage } from './Base'
import { isAllSerializedCustomTables } from './Validators'

type SerializedCustomTable = [
    /** @see {CustomizedTable['page']} */
    page: number,
    /** @see {CustomizedTable['pageSize']} */
    pageSize: number,
    /** @see {CustomizedTable['sort']} */
    sort: [string, number] | 0,
    /** @see {CustomizedTable['search']} */
    search: string
]

export type AllPersistedTables = ReadonlyRecord<string, Partial<CustomizedTable<ReadonlyRecord<string, unknown>>>>
export type AllSerializedCustomTables = ReadonlyRecord<string, SerializedCustomTable>

export class WebStorageTablePersistenceService extends BaseWebStorage<AllPersistedTables, AllSerializedCustomTables> implements TablePersistenceService {
    protected readonly fallbackValue = {}

    protected readonly key = 'cwTables'

    // eslint-disable-next-line class-methods-use-this
    protected toSerializable (allTables: AllPersistedTables): AllSerializedCustomTables {
        return objectEntries(allTables).reduce<AllSerializedCustomTables>(
            (r, [id, { page, pageSize, sort, search }]) => ({
                ...r,
                [id]: [page !== undefined ? page : 0, pageSize !== undefined ? pageSize : 0, sort ? [sort.column, Number(sort.asc)] : 0, search || ''],
            }),
            {}
        )
    }

    // eslint-disable-next-line class-methods-use-this
    protected isValidSerializable (unknownValue: unknown): unknownValue is AllSerializedCustomTables {
        return isAllSerializedCustomTables(unknownValue)
    }

    // eslint-disable-next-line class-methods-use-this
    protected fromSerializable (serialized: AllSerializedCustomTables): AllPersistedTables {
        return objectEntries(serialized).reduce((r, [id, unknownValue]) => {
            const [page, pageSize, sort, search] = unknownValue

            type Table = ValuesType<AllPersistedTables>
            const table: Mutable<Table> = {}

            if (page > 0) {
                table.page = page
            }

            if (pageSize > 0) {
                table.pageSize = pageSize
            }

            if (isArray(sort) && sort.length === 2 && typeof sort[0] === 'string' && typeof sort[1] === 'number') {
                table.sort = { column: sort[0], asc: Boolean(sort[1]) } as Table['sort']
            }

            if (search) {
                table.search = search
            }

            return { ...r, [id]: table }
        }, {})
    }

    retrieveTable<L extends ReadonlyRecord<string, unknown>> (id: string): Partial<CustomizedTable<L>> {
        return this.retrieveDeserialize()[id] ?? {}
    }

    persistTable<L extends ReadonlyRecord<string, unknown>> (id: string, table: Partial<CustomizedTable<L>>): void {
        const deserialized = this.retrieveDeserialize()
        this.persistSerialize({ ...deserialized, [id]: table as Partial<CustomizedTable<ReadonlyRecord<string, unknown>>> })
    }

    flush (): void {
        this.remove()
    }
}
